/**
 * Fait : les appels du client qui portent sur un PROJET et ses participants —
 *   lister, créer, renommer, archiver, mettre à la corbeille, et tout ce qui
 *   touche aux membres : les lister, partager, changer un niveau, retirer
 *   quelqu'un ou partir soi-même.
 *
 *   Sortis de `api.ts` le 2026-08-23, au lot 4, quand ce fichier a dépassé le
 *   plafond de lignes du dépôt. Le découpage suit celui qui existait déjà pour
 *   les fils (`api-fil.ts`) : un module par famille de choses.
 *
 * Ne fait pas : il ne décide d'aucun droit et n'affiche rien. Ce que le serveur
 *   refuse, il le rend tel quel — le client ne cache jamais un refus derrière
 *   une jolie phrase inventée.
 */

import type { NiveauDeMembre } from '@aymoai/shared';

import { appelle } from './appel.ts';
import type { MembreVu, ProjetVu, ReferentPossibleVu } from './api.ts';

/** La liste « Partagé avec moi ». */
export function listeLesProjets() {
  return appelle<ProjetVu[]>('/api/projets');
}

/**
 * Crée un projet.
 *
 * LE RÉFÉRENT N'EST PAS ENVOYÉ QUAND IL N'Y EN A PAS, et c'est le serveur qui
 * décide s'il en fallait un : lui seul connaît le rôle du demandeur, et son
 * refus NOMME le choix attendu au lieu de dire « la demande est mal formée ».
 * L'écran ne réécrit pas cette règle — il pose le champ et rend le refus.
 */
export function creeUnProjet(
  nom: string,
  description?: string,
  referent?: { readonly compteId: string; readonly niveau: NiveauDeMembre },
) {
  return appelle<{ id: string }>('/api/projets', {
    methode: 'POST',
    // La clé n'est envoyée que remplie : le schéma de la demande refuse
    // toute clé inconnue, et une description vide n'est pas une description.
    corps: {
      nom,
      ...(description === undefined || description === '' ? {} : { description }),
      ...(referent === undefined
        ? {}
        : { referentId: referent.compteId, niveauDuReferent: referent.niveau }),
    },
  });
}

/** Les personnes qu'un membre peut désigner comme référent (`D-031`). */
export function listeLesReferentsPossibles() {
  return appelle<ReferentPossibleVu[]>('/api/referents-possibles');
}

/** Renomme un projet — la version est obligatoire (arbitrage, critère 21). */
export function renommeLeProjet(id: string, nom: string, version: number, description?: string) {
  return appelle<void>(`/api/projets/${id}`, {
    methode: 'PATCH',
    // Ici la chaîne VIDE est envoyée : c'est ainsi qu'on efface une
    // description. Seule son absence signifie « n'y touche pas ».
    corps: { nom, version, ...(description === undefined ? {} : { description }) },
  });
}

/**
 * Archive ou désarchive un projet — même exigence de version qu'un renommage.
 * Le geste se rattrape d'un clic : c'est pourquoi il n'a pas de fenêtre de
 * confirmation, contrairement à la mise à la corbeille juste en dessous.
 */
export function archiveLeProjet(id: string, archive: boolean, version: number) {
  return appelle<void>(`/api/projets/${id}`, { methode: 'PATCH', corps: { archive, version } });
}

/**
 * Met un projet à la corbeille : il quitte la vue de TOUS ses participants, et
 * pas un document n'est effacé. Le serveur refuse une seconde mise à la
 * corbeille au lieu de répondre « c'est fait » — l'écran a donc un refus à
 * montrer, et il le montre.
 */
export function metLeProjetALaCorbeille(id: string) {
  return appelle<void>(`/api/projets/${id}`, { methode: 'DELETE' });
}

/** Les membres d'un projet. */
export function listeLesMembres(id: string) {
  return appelle<MembreVu[]>(`/api/projets/${id}/membres`);
}

/** Change le niveau d'un membre — le propriétaire seul en a le droit. */
export function changeLeNiveauDUnMembre(id: string, membreId: string, niveau: NiveauDeMembre) {
  return appelle<void>(`/api/projets/${id}/membres/${membreId}`, {
    methode: 'PATCH',
    corps: { niveau },
  });
}

/**
 * Retire quelqu'un d'un projet — ou s'en retire soi-même, c'est la MÊME route.
 * Quitter un projet n'est pas l'administrer : le serveur distingue les deux, et
 * il refuse le départ d'un référent que personne ne peut remplacer (`D-023`,
 * `D-032`). L'écran montre ce refus tel quel.
 */
export function retireDuProjet(id: string, membreId: string) {
  return appelle<void>(`/api/projets/${id}/membres/${membreId}`, { methode: 'DELETE' });
}

/** Partage un projet avec une adresse. */
export function partageLeProjet(id: string, adresseElectronique: string, niveau: NiveauDeMembre) {
  return appelle<void>(`/api/projets/${id}/membres`, {
    methode: 'POST',
    corps: { adresseElectronique, niveau },
  });
}
